import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useClients } from "../../hooks/useClients";
import Reveal from "../Reveal";

/**
 * About-page client showcase — uses the same admin-managed client list as the
 * homepage marquee (`useClients`). Falls back to bundled real-brand logos
 * shipped in /public/brand-clients/ when the DB is empty.
 *
 * Generic placeholder names like "Aurora / Verde / Citrus" have been removed —
 * Yastık Medya only displays brands it has actually worked with.
 */
const FALLBACK_CLIENTS = [
  { id: "bahcesehir-uni", name: "Bahçeşehir Üniversitesi", logo: "/brand-clients/bahcesehir-uni.png", website: "https://bau.edu.tr/" },
  { id: "ugur-okullari", name: "Uğur Okulları", logo: "/brand-clients/ugur-okullari.png", website: "https://www.ugurokullari.k12.tr/" },
  { id: "praktiker", name: "Praktiker", logo: "/brand-clients/praktiker.png", website: "https://praktiker.com.tr/" },
  { id: "izocam", name: "İzocam", logo: "/brand-clients/izocam.png", website: "https://www.izocam.com.tr/" },
  { id: "amorium-hotel", name: "Amorium Hotel", logo: "/brand-clients/amorium-hotel.png", website: "https://amoriumhotel.com/" },
  { id: "muscle-station", name: "Muscle Station", logo: "/brand-clients/muscle-station.webp", website: "https://www.musclestation.com.tr/" },
  { id: "hb-dijital-logo", name: "HB Dijital", logo: "/brand-clients/hb-dijital-logo.png", website: "https://hbdijitalbaski.com.tr/" },
  { id: "bigistinvest", name: "Bigist Invest", logo: "/brand-clients/bigistinvest.png", website: "https://www.instagram.com/bigistinvest/" },
  { id: "potensas", name: "Potensas", logo: "/brand-clients/potensas.png", website: "https://www.potensas.com/" },
  { id: "uskudar-sinav", name: "Üsküdar Sınav Koleji", logo: "/brand-clients/uskudar-sinav.webp", website: "https://www.uskudarsinavkoleji.com/" },
  { id: "tssf", name: "TSSF", logo: "/brand-clients/tssf.png", website: "https://tssf.gov.tr/" },
  { id: "visamerika", name: "Visamerika", logo: "/brand-clients/visamerika.png", website: "https://www.visamerika.com/" },
];

export default function ClientLogos() {
  const { t } = useLanguage();
  const dynamic = useClients();
  const list = (dynamic && dynamic.length > 0 ? dynamic : FALLBACK_CLIENTS).filter((c) => c.logo);

  return (
    <section data-testid="about-clients-section" className="bg-cream py-section border-t border-mist">
      <div className="container-page">
        <Reveal as="div" className="text-center max-w-xl mx-auto">
          <span className="eyebrow justify-center">
            <span className="h-1 w-1 rounded-full bg-brand-pink" />
            {t("about.clients_eyebrow")}
          </span>
          <h2 className="mt-5 font-display text-h1 font-semibold text-ink">
            {t("about.clients_title")}
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-10 items-center">
          {list.slice(0, 12).map((c, i) => (
            <Reveal as="a" key={c.id || c.name} delay={i * 0.04}
              href={c.website || "#"} target="_blank" rel="noopener noreferrer" title={c.name}
              className="group flex items-center justify-center h-16 sm:h-20"
              data-testid={`client-logo-${i + 1}`}
            >
              <img src={c.logo} alt={c.name} loading="lazy"
                className="max-h-10 sm:max-h-12 w-auto object-contain
                           grayscale opacity-55 transition-[filter,opacity,transform] duration-500
                           group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
